import { Stack, Typography } from '@mui/joy';
import { t } from 'i18next';
import { useState } from 'react';
import { UseFormReset } from 'react-hook-form';
import { Trans, useTranslation } from 'react-i18next';
import * as yup from 'yup';

import { useContactGroupActions } from 'contacts/hooks';
import { IContactGroup } from 'contacts/types';
import { Confirmation, ExpandableList, Form, FormField } from 'core/components';
import { InputType } from 'core/enums';
import { FormProvider } from 'core/states';
import { numberSchemaWithTransform } from 'core/yup';
import { Color, Level } from 'theme/enums';

import { IMergeContactGroupsForm } from './types';

interface IProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmitSuccess?: () => void;
  contactGroups: Partial<IContactGroup>[];
}

const schema = () =>
  yup.object({
    contactGroup: yup
      .object({
        id: numberSchemaWithTransform().nullable(),
        name: yup.string().nullable(),
      })
      .nullable()
      .required(t('messageSelectCompanyNameToMerge')),
  });

export const MergeContactGroups = ({ isOpen, onClose, onSubmitSuccess, contactGroups }: IProps) => {
  const [isConfirmationOpen, setIsConfirmationOpen] = useState(false);
  const [formData, setFormData] = useState<IMergeContactGroupsForm | null>(null);

  const { t } = useTranslation();

  const { mergeContactGroups } = useContactGroupActions();

  const onSubmit = () => async (data: IMergeContactGroupsForm) => {
    setFormData(data);

    setIsConfirmationOpen(true);
  };

  const onConfirm = async () => {
    setIsConfirmationOpen(false);

    const payload = {
      mainContactGroupId: formData?.contactGroup?.id,
      ids: contactGroups
        .filter((contactGroup) => contactGroup.id !== formData?.contactGroup?.id)
        .map((contactGroup) => contactGroup.id),
    };

    const { success } = await mergeContactGroups(payload);

    if (!success) {
      return;
    }

    setTimeout(() => {
      handleOnClose();

      onSubmitSuccess && onSubmitSuccess();
    }, 1000);
  };

  const getConfirmationMessage = () => {
    const mainContactGroup = contactGroups.find(
      (contactGroup) => contactGroup.id === formData?.contactGroup?.id
    );

    const filteredGroups = contactGroups.filter(
      (contactGroup) => contactGroup.id !== formData?.contactGroup?.id
    );

    return (
      <Stack gap={1}>
        <Typography level={Level.BODY_MD}>
          <Trans
            i18nKey="messageConfirmMergeContactGroups"
            components={{
              countWrapper: <Typography fontWeight={600} />,
              nameWrapper: <Typography fontWeight={600} />,
            }}
            values={{
              mainCompany: mainContactGroup?.name,
              count: filteredGroups.length,
            }}
          />
        </Typography>

        <ExpandableList<IContactGroup>
          items={filteredGroups as IContactGroup[]}
          itemRenderer={(item) => (
            <Typography level={Level.BODY_MD} fontWeight={600}>
              {item.name}
            </Typography>
          )}
        />
        <Typography color={Color.DANGER} level={Level.BODY_SM} fontStyle={'italic'}>
          {t('messageConfirmMergeContactGroupsNote')}
        </Typography>
      </Stack>
    );
  };

  const onCancelConfirmation = () => {
    setIsConfirmationOpen(false);

    setFormData(null);
  };

  const handleOnClose = (reset?: UseFormReset<IMergeContactGroupsForm>) => {
    onClose();
    reset && reset({ contactGroup: null });
  };

  return (
    <FormProvider schema={schema()} defaultValues={{ contactGroup: null }}>
      <Form
        title={t('titleMergeContactGroups')}
        onSubmit={onSubmit}
        onClose={handleOnClose}
        isOpen={isOpen}
        submitBtnLabel={t('buttonLabelMergeContactGroups')}
      >
        <FormField
          key={formData?.contactGroup?.id}
          name="contactGroup"
          type={InputType.AUTOCOMPLETE}
          inputProps={{
            label: t('labelMergeContactGroup'),
            defaultOptions: contactGroups.map((contactGroup) => ({
              id: contactGroup.id,
              name: contactGroup.name,
            })),
            getLabel: (option: { name: string }) => option.name,
            required: true,
          }}
        />
      </Form>

      <Confirmation
        isOpen={isConfirmationOpen}
        color={Color.DANGER}
        onCancel={onCancelConfirmation}
        onConfirm={onConfirm}
        title={t('titleMergeContactGroups')}
        message={getConfirmationMessage()}
        confirmBtnLabel={t('buttonLabelConfirmMergeContactGroups')}
      />
    </FormProvider>
  );
};
